import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  FormGroup,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sv } from 'date-fns/locale';
import { BRAND_COLORS, alpha } from '../styles/theme';
import { useReportFiltersQuery } from './Reports.queries';
import { useGenerateReport } from './Reports.mutations';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useTranslation } from '../i18n';
import { ReportFilters } from '../types/api';
import { formatDateToLocalString } from '../utils/dateUtils';

interface ReportGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ReportGenerationDialog: React.FC<ReportGenerationDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess } = useErrorHandler();

  // Form state
  const [title, setTitle] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedDrones, setSelectedDrones] = useState<string[]>([]);
  const [selectedPayloads, setSelectedPayloads] = useState<number[]>([]);
  const [selectedPilots, setSelectedPilots] = useState<string[]>([]);
  const [includeIncidents, setIncludeIncidents] = useState<boolean>(false);
  // New content type checkboxes
  const [includeFlightHours, setIncludeFlightHours] = useState<boolean>(true);
  const [includeMaintenance, setIncludeMaintenance] = useState<boolean>(false);
  const [selectedMaintenanceTypes, setSelectedMaintenanceTypes] = useState<string[]>([]);

  // Error state for the inline alert
  const [error, setError] = useState<string>('');

  // Filter options are fetched (and cached) only while the dialog is open.
  const filtersQuery = useReportFiltersQuery(open);
  const filterOptions = filtersQuery.data ?? null;
  const loadingOptions = filtersQuery.isLoading;
  const generateReport = useGenerateReport();

  // Filter-load errors are toasted by the global query handler (lib/queryClient).

  // Set default dates (last 30 days) each time the dialog opens.
  useEffect(() => {
    if (open) {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setStartDate(start);
      setEndDate(end);
    }
  }, [open]);

  const handleGenerate = async () => {
    if (!title.trim()) {
      setError(t('reports.titleRequired'));
      return;
    }
    if (!startDate || !endDate) {
      setError(t('reports.datesRequired'));
      return;
    }
    if (startDate > endDate) {
      setError(t('reports.invalidDateRange'));
      return;
    }
    // Validate at least one content type is selected
    if (!includeFlightHours && !includeMaintenance && !includeIncidents) {
      setError(t('reports.selectAtLeastOne'));
      return;
    }

    setError('');

    try {
      const filters: ReportFilters = {
        drone_serials: selectedDrones.length > 0 ? selectedDrones : null,
        start_date: formatDateToLocalString(startDate),
        end_date: formatDateToLocalString(endDate),
        // Flight hours specific filters (only relevant if flight hours included)
        payload_ids: includeFlightHours && selectedPayloads.length > 0 ? selectedPayloads : null,
        pilot_ids: includeFlightHours && selectedPilots.length > 0 ? selectedPilots : null,
        include_incidents: includeIncidents,
        // Content type flags
        include_flight_hours: includeFlightHours,
        include_maintenance: includeMaintenance,
        // Maintenance specific filters
        maintenance_type_ids:
          includeMaintenance && selectedMaintenanceTypes.length > 0
            ? selectedMaintenanceTypes
            : null,
      };

      await generateReport.mutateAsync({ title, filters });
      showSuccess(t('reports.generateSuccess'));
      resetForm();
      onSuccess();
    } catch {
      // Toast (with server detail) is raised by the global mutation handler;
      // the inline alert stays a stable, user-friendly retry prompt.
      setError(t('reports.generateFailed'));
    }
  };

  const resetForm = () => {
    setTitle('');
    setStartDate(null);
    setEndDate(null);
    setSelectedDrones([]);
    setSelectedPayloads([]);
    setSelectedPilots([]);
    setIncludeIncidents(false);
    setIncludeFlightHours(true);
    setIncludeMaintenance(false);
    setSelectedMaintenanceTypes([]);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: BRAND_COLORS.forestGreen.main }}>
        {t('reports.generateReportTitle')}
      </DialogTitle>
      <DialogContent>
        {loadingOptions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Report Title */}
            <TextField
              label={t('reports.reportTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder={t('reports.titlePlaceholder')}
            />

            {/* Date Range */}
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label={t('reports.startDate')}
                  value={startDate}
                  onChange={(newValue: Date | null) => setStartDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label={t('reports.endDate')}
                  value={endDate}
                  onChange={(newValue: Date | null) => setEndDate(newValue)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>

            {/* Content Type Selection */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {t('reports.includeInReport')}
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeFlightHours}
                    onChange={(e) => setIncludeFlightHours(e.target.checked)}
                    sx={{
                      color: BRAND_COLORS.forestGreen.main,
                      '&.Mui-checked': { color: BRAND_COLORS.forestGreen.main },
                    }}
                  />
                }
                label={t('reports.includeFlightHours')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeMaintenance}
                    onChange={(e) => setIncludeMaintenance(e.target.checked)}
                    sx={{
                      color: BRAND_COLORS.forestGreen.main,
                      '&.Mui-checked': { color: BRAND_COLORS.forestGreen.main },
                    }}
                  />
                }
                label={t('reports.includeMaintenanceData')}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeIncidents}
                    onChange={(e) => setIncludeIncidents(e.target.checked)}
                    sx={{
                      color: BRAND_COLORS.forestGreen.main,
                      '&.Mui-checked': { color: BRAND_COLORS.forestGreen.main },
                    }}
                  />
                }
                label={t('reports.includeIncidents')}
              />
            </FormGroup>
            <Divider sx={{ my: 1 }} />

            {/* Drone Selection - always visible */}
            <FormControl fullWidth>
              <InputLabel>{t('reports.selectDrones')}</InputLabel>
              <Select
                multiple
                value={selectedDrones}
                onChange={(e) => setSelectedDrones(e.target.value as string[])}
                input={<OutlinedInput label={t('reports.selectDrones')} />}
                renderValue={(selected) =>
                  selected.length === 0 ? t('reports.allDrones') : selected.join(', ')
                }
              >
                {filterOptions?.drones.map((drone) => (
                  <MenuItem key={drone.serial_number} value={drone.serial_number}>
                    <Checkbox checked={selectedDrones.includes(drone.serial_number)} />
                    <ListItemText primary={drone.serial_number} />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary">
                {t('reports.leaveEmptyForAll')}
              </Typography>
            </FormControl>

            {/* Flight Hours Specific Filters - only show if flight hours included */}
            {includeFlightHours && (
              <>
                {/* Payload Selection */}
                <FormControl fullWidth>
                  <InputLabel>{t('reports.selectPayloads')}</InputLabel>
                  <Select
                    multiple
                    value={selectedPayloads}
                    onChange={(e) => setSelectedPayloads(e.target.value as number[])}
                    input={<OutlinedInput label={t('reports.selectPayloads')} />}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t('reports.allPayloads')
                        : filterOptions?.payloads
                            .filter((p) => selected.includes(p.id))
                            .map((p) => p.name)
                            .join(', ')
                    }
                  >
                    {filterOptions?.payloads.map((payload) => (
                      <MenuItem key={payload.id} value={payload.id}>
                        <Checkbox checked={selectedPayloads.includes(payload.id)} />
                        <ListItemText primary={payload.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Pilot Selection */}
                <FormControl fullWidth>
                  <InputLabel>{t('reports.selectPilots')}</InputLabel>
                  <Select
                    multiple
                    value={selectedPilots}
                    onChange={(e) => setSelectedPilots(e.target.value as string[])}
                    input={<OutlinedInput label={t('reports.selectPilots')} />}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? t('reports.allPilots')
                        : filterOptions?.pilots
                            .filter((p) => selected.includes(p.id))
                            .map((p) => p.name)
                            .join(', ')
                    }
                  >
                    {filterOptions?.pilots.map((pilot) => (
                      <MenuItem key={pilot.id} value={pilot.id}>
                        <Checkbox checked={selectedPilots.includes(pilot.id)} />
                        <ListItemText primary={pilot.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {/* Maintenance Specific Filters - only show if maintenance included */}
            {includeMaintenance && (
              <FormControl fullWidth>
                <InputLabel>{t('reports.maintenanceTypes')}</InputLabel>
                <Select
                  multiple
                  value={selectedMaintenanceTypes}
                  onChange={(e) => setSelectedMaintenanceTypes(e.target.value as string[])}
                  input={<OutlinedInput label={t('reports.maintenanceTypes')} />}
                  renderValue={(selected) =>
                    selected.length === 0
                      ? t('reports.allMaintenanceTypes')
                      : filterOptions?.maintenance_types
                          .filter((mt) => selected.includes(mt.value))
                          .map((mt) => mt.label)
                          .join(', ')
                  }
                >
                  {filterOptions?.maintenance_types.map((mt) => (
                    <MenuItem key={mt.value} value={mt.value}>
                      <Checkbox checked={selectedMaintenanceTypes.includes(mt.value)} />
                      <ListItemText primary={mt.label} />
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary">
                  {t('reports.maintenanceTypesHelper')}
                </Typography>
              </FormControl>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generateReport.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={generateReport.isPending || loadingOptions}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.8),
            },
          }}
        >
          {generateReport.isPending ? <CircularProgress size={24} /> : t('reports.generate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportGenerationDialog;
